<template>
  <div class="box">
    <!-- Header with search and add button -->
    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <h3 class="title">Banking Information</h3>
        </div>
      </div>
      <div class="level-right">
        <!-- <div class="level-item">
          <div class="field" style="min-width: 250px; margin-right: 1rem;">
            <div class="control has-icons-left">
              <input
                v-model="searchTerm"
                class="input"
                type="text"
                placeholder="Search by bank name..."
              />
              <span class="icon is-left">
                <i class="fas fa-search"></i>
              </span>
            </div>
          </div>
        </div> -->
        <div v-if="!isAuthorizedRep" class="level-item">
          <button class="button is-primary" @click="handleAddPayment">
            <span class="icon">
              <i class="fas fa-plus"></i>
            </span>
            <span>Add Banking Information</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Payment Method Cards Grid -->
    <div class="payment-cards-grid">
      <div class="columns is-multiline">
        <div
          v-for="payment in filteredPaymentMethods"
          :key="payment.rid"
          class="column is-half-tablet is-one-third-desktop"
        >
          <div class="payment-card">
            <div class="payment-card-content">
              <!-- Top section: Bank icon & account info -->
              <div class="payment-info">
                <div class="bank-icon">
                  <i class="fas fa-university"></i>
                </div>
                <div class="account-details">
                  <!-- Bank name and actions row -->
                  <div class="title-row">
                    <div class="account-title">
                      <strong>{{ payment.bank_name }}</strong>
                    </div>
                    <!-- Action buttons -->
                    <div class="card-actions" @click.stop>
                      <!-- Edit button (only for expired payments) -->
                      <button
                        v-if="canModifyPayment(payment) && !isAuthorizedRep"
                        @click.stop="editPayment(payment)"
                        class="edit-icon-btn"
                        aria-label="Edit payment method"
                      >
                        <i class="fas fa-pencil-alt"></i>
                      </button>
                      <!-- Deactivate button (only for approved payments) -->
                      <button
                        v-if="canDeactivatePayment(payment) && !isAuthorizedRep"
                        @click.stop="handleDeactivatePayment(payment)"
                        class="deactivate-icon-btn"
                        aria-label="Deactivate payment method"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Status indicators -->
                  <div v-if="payment.bank_info_status === 'Pending'" class="status-badge pending">
                    (Pending Approval)
                  </div>
                  <div v-else-if="payment.bank_info_status === 'Expired'" class="status-badge expired">
                    (Expired)
                  </div>
                  <div v-else-if="payment.bank_info_status === 'Inactive'" class="status-badge deactivated">
                    (Deactivated)
                  </div>
                  <div v-else-if="payment.bank_expiration_date && payment.bank_info_status === 'Approved'" class="status-badge approved">
                    Approved - Expires {{ payment.bank_expiration_date }}
                  </div>

                  <!-- Account details (grayed out) -->
                  <div class="routing-number medium-gray">
                    Routing: ****{{ payment.routing_number ? payment.routing_number.slice(-4) : '' }}
                  </div>
                  <div class="account-number medium-gray">
                    Account ending in {{ payment.last_four_account_number }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No results message -->
        <div v-if="filteredPaymentMethods.length === 0" class="column is-full">
          <div class="has-text-centered" style="padding: 3rem;">
            <p class="medium-gray">
              <i class="fas fa-credit-card fa-3x" style="color: #cfcfcf; margin-bottom: 1rem;"></i><br />
              No banking information found
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Payment Modal -->
    <add-payment-modal
      :is-active="isPaymentModalActive"
      :edit-mode="isEditMode"
      :payment-data="selectedPayment"
      @close="closePaymentModal"
      @submit="handlePaymentSubmit"
    />

    <!-- Deactivate Payment Modal -->
    <deactivate-payment-modal
      :is-active="isDeactivatePaymentModalActive"
      :payment="selectedPaymentForDeactivation || {}"
      @close="closeDeactivatePaymentModal"
      @confirm="handleDeactivateConfirm"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { filterPaymentMethods } from "../mock-data/payments.js";
import AddPaymentModal from "./AddPaymentModal.vue";
import DeactivatePaymentModal from "./DeactivatePaymentModal.vue";

export default {
  name: "PaymentsTable",
  components: {
    AddPaymentModal,
    DeactivatePaymentModal,
  },
  data() {
    return {
      searchTerm: "",
      isPaymentModalActive: false,
      isEditMode: false,
      selectedPayment: null,
      isDeactivatePaymentModalActive: false,
      selectedPaymentForDeactivation: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["userInfo"]),
    ...mapGetters("dashboard", ["payments"]),
    filteredPaymentMethods() {
      if (!this.searchTerm) {
        return this.payments;
      }
      return filterPaymentMethods(this.searchTerm);
    },
    isAuthorizedRep() {
      return this.userInfo?.qbUser?.role === "ar";
    },
  },
  methods: {
    handleRowClick(payment) {
      console.log("Payment row clicked:", payment);
      this.$emit("row-click", payment);
    },
    canModifyPayment(payment){
      return payment.bank_info_status === 'Expired'
    },
    editPayment(payment) {
      console.log("Edit payment:", payment);
      this.selectedPayment = payment;
      this.isEditMode = true;
      this.isPaymentModalActive = true;
    },
    handleAddPayment() {
      console.log("Add new payment method");
      this.selectedPayment = null;
      this.isEditMode = false;
      this.isPaymentModalActive = true;
    },
    closePaymentModal() {
      this.isPaymentModalActive = false;
      this.isEditMode = false;
      this.selectedPayment = null;
    },
    handlePaymentSubmit(paymentData) {
      this.closePaymentModal();
      if (this.isEditMode) {
        this.$emit("payment-edited", paymentData);
      } else {
        this.$emit("payment-added", paymentData);
      }
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${year}`;
    },
    canDeactivatePayment(payment) {
      return payment.bank_info_status === 'Approved';
    },
    handleDeactivatePayment(payment) {
      this.selectedPaymentForDeactivation = payment;
      this.isDeactivatePaymentModalActive = true;
    },
    closeDeactivatePaymentModal() {
      this.isDeactivatePaymentModalActive = false;
      this.selectedPaymentForDeactivation = null;
    },
    handleDeactivateConfirm(payment) {
      // Modal handles the submission and only emits confirm on success
      // Close the modal
      this.closeDeactivatePaymentModal();

      // Emit event to parent if needed
      this.$emit("payment-deactivated", payment);
    },
  },
};
</script>

<style scoped>
/* Custom styling to match PhilaUI */
.medium-gray {
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
}

/* Payment Cards Grid Container */
.payment-cards-grid {
  margin-top: 1rem;
}

/* Individual Payment Card */
.payment-card {
  background: #ffffff;
  border: 2px solid #cfcfcf; /* Sidewalk Gray - Philly Style Guide */
  border-radius: 8px;
  padding: 1.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Card Content Layout */
.payment-card-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Bank info container */
.payment-info {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
}

.bank-icon {
  width: 48px;
  height: 48px;
  background: #2176d2; /* Ben Franklin Blue - Philly Style Guide */
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.account-details {
  flex: 1;
  min-width: 0;
}

/* Title row with actions */
.title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.account-title {
  font-size: 1rem;
  color: #000000;
  flex: 1;
  line-height: 1.3;
}

/* Card actions (top right) */
.card-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.edit-icon-btn {
  background: transparent;
  border: none;
  color: #2176d2; /* Ben Franklin Blue - Philly Style Guide */
  cursor: pointer;
  padding: 0.25rem;
  font-size: 1rem;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-icon-btn:hover {
  color: #0f4d90; /* Dark Ben Franklin Blue - Philly Style Guide */
}

.deactivate-icon-btn {
  background: transparent;
  border: none;
  color: #cc3000; /* Love Park Red - Philly Style Guide */
  cursor: pointer;
  padding: 0.25rem;
  font-size: 1rem;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.deactivate-icon-btn:hover {
  color: #a82400; /* Love Park Red Dark - Philly Style Guide */
}


/* Status badges */
.status-badge {
  font-size: 0.875rem;
  font-weight: 400;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.status-badge.pending {
  color: #f99300; /* Flyers Orange - Philly Style Guide */
}

.status-badge.approved {
  color: #3a833c; /* Phanatic Green - Philly Style Guide */
}

.status-badge.expired {
  color: #cc3000; /* Love Park Red - Philly Style Guide */
}

.status-badge.deactivated {
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
}

/* Account details */
.account-number {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  line-height: 1.3;
}

.routing-number {
  font-size: 0.875rem;
  line-height: 1.3;
}

/* Responsive */
@media (max-width: 768px) {
  .bank-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .account-title {
    font-size: 0.9rem;
  }

  .edit-icon-btn {
    font-size: 0.9rem;
  }
}
</style>
